import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Close,
  Radio,
  Schedule,
  MusicNote,
  Headphones,
  QueueMusic
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { programService } from '../../services/programService';
import { fixImageUrl } from '../../utils/imageUtils';

const ProgrammingModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedDay, setSelectedDay] = useState(0);
  const [currentDay, setCurrentDay] = useState(0);

  // Dias da semana com versão completa e compacta para mobile
  const daysOfWeek = [
    { key: 'sunday', full: 'Domingo', short: 'Dom' },
    { key: 'monday', full: 'Segunda-feira', short: 'Seg' },
    { key: 'tuesday', full: 'Terça-feira', short: 'Ter' },
    { key: 'wednesday', full: 'Quarta-feira', short: 'Qua' },
    { key: 'thursday', full: 'Quinta-feira', short: 'Qui' },
    { key: 'friday', full: 'Sexta-feira', short: 'Sex' },
    { key: 'saturday', full: 'Sábado', short: 'Sáb' },
  ];

  // Sincronizar dia atual no fuso da rádio (América/São Paulo)
  useEffect(() => {
    if (open) {
      const spDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
      const spDay = spDate.getDay();
      setCurrentDay(spDay);
      setSelectedDay(spDay);
    }
  }, [open]);

  const selectedKey = daysOfWeek[selectedDay]?.key || 'friday';

  // Buscar programação para o dia selecionado
  const { data: programmingData, isLoading, error } = useQuery({
    queryKey: ['programming', selectedKey],
    queryFn: () => programService.getProgrammingByDay(selectedKey),
    enabled: open,
    staleTime: 30000,
  });

  // Buscar programa ao vivo para identificar NO AR com precisão
  const { data: liveProgramData } = useQuery({
    queryKey: ['liveProgram'],
    queryFn: () => programService.getLiveProgram(),
    enabled: open,
    refetchInterval: 15000,
  });

  const responseData = programmingData?.data?.data || programmingData?.data || {};
  const djPrograms = Array.isArray(responseData.programs) ? responseData.programs : [];
  const defaultPrograms = Array.isArray(responseData.defaultPrograms) ? responseData.defaultPrograms : [];
  const automaticProgramming = responseData.automaticProgramming;

  // Se houver defaultPrograms (AutoDJ cadastrado na grade), usamos eles.
  // O fallback genérico de 24h só é exibido se não houver NENHUM programa no dia.
  const autoPrograms = defaultPrograms.length > 0
    ? defaultPrograms
    : (djPrograms.length === 0 && automaticProgramming ? [automaticProgramming] : []);

  const liveData = liveProgramData?.data?.data;
  const isToday = selectedDay === currentDay;

  const handleTabChange = (event, newValue) => {
    setSelectedDay(newValue);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.slice(0, 5);
  };

  const isCurrentDay = (dayIndex) => dayIndex === currentDay;

  // Lista unificada ordenada para detectar ao vivo e a seguir
  const allDayPrograms = [...djPrograms, ...autoPrograms].sort((a, b) =>
    (a.start_time || '').localeCompare(b.start_time || '')
  );

  const checkIsLive = (program) => {
    if (!isToday || !program) return false;

    // 1. Prioridade para o programa retornado pelo backend como live
    if (liveData?.name) {
      if (program.name && program.name.trim().toLowerCase() === liveData.name.trim().toLowerCase()) {
        return true;
      }
      if (program.id && (program.id === liveData.id || program.id === liveData._id)) {
        return true;
      }
    }

    // 2. Não marcar bloco de 24h como NO AR se houver programas específicos
    if (program.start_time === '00:00:00' && program.end_time === '23:59:59' && (djPrograms.length > 0 || defaultPrograms.length > 0)) {
      return false;
    }

    // 3. Checagem por horário de São Paulo
    if (!program.start_time || !program.end_time) return false;
    const currentSPTime = new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Sao_Paulo', hour12: false });
    const start = program.start_time;
    const end = program.end_time;
    if (start > end) {
      return currentSPTime >= start || currentSPTime <= end;
    }
    return currentSPTime >= start && currentSPTime <= end;
  };

  const currentLiveProgram = allDayPrograms.find(p => checkIsLive(p));

  const checkIsNext = (program) => {
    if (!isToday || !program || checkIsLive(program)) return false;

    if (currentLiveProgram) {
      const activeEnd = currentLiveProgram.end_time || '';
      const activeStart = currentLiveProgram.start_time || '';

      if (activeEnd) {
        const next = allDayPrograms.find(
          p => p.id !== currentLiveProgram.id && p.name !== currentLiveProgram.name && p.start_time >= activeEnd
        );
        if (next) return (next.id && next.id === program.id) || (next.name === program.name);
      }

      if (activeStart) {
        const next = allDayPrograms.find(
          p => p.id !== currentLiveProgram.id && p.name !== currentLiveProgram.name && p.start_time > activeStart
        );
        if (next) return (next.id && next.id === program.id) || (next.name === program.name);
      }
    }

    const currentSPTime = new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Sao_Paulo', hour12: false });
    const nextCandidate = allDayPrograms.find(
      p => (!currentLiveProgram || (p.id !== currentLiveProgram.id && p.name !== currentLiveProgram.name)) && p.start_time > currentSPTime
    );
    return Boolean(nextCandidate && ((nextCandidate.id && nextCandidate.id === program.id) || (nextCandidate.name === program.name)));
  };

  const renderProgramCard = (program, isAutomatic = false) => {
    const isLive = checkIsLive(program);
    const isNext = checkIsNext(program);

    const avatarSrc = isAutomatic
      ? (program.banner_image && !program.banner_image.includes('default.png') ? fixImageUrl(program.banner_image) : null)
      : fixImageUrl(program.dj?.profile_image || program.banner_image);

    const djDisplayName = isAutomatic
      ? (program.dj?.name || 'Programação Automática')
      : (program.dj?.artisticName || program.dj?.name || 'DJ Convidado');

    return (
      <Card
        key={`${isAutomatic ? 'auto' : 'dj'}-${program.id || program._id || program.name}`}
        sx={{
          mb: 1.5,
          p: { xs: 1.3, sm: 2 },
          bgcolor: isLive
            ? 'linear-gradient(135deg, rgba(255, 107, 53, 0.16) 0%, rgba(20, 20, 24, 0.95) 100%)'
            : isNext
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(20, 20, 24, 0.95) 100%)'
            : 'rgba(255, 255, 255, 0.03)',
          border: isLive
            ? '1.5px solid #FF6B35'
            : isNext
            ? '1.5px solid rgba(16, 185, 129, 0.6)'
            : '1px solid rgba(255, 255, 255, 0.07)',
          borderLeft: isLive
            ? '4px solid #FF6B35'
            : isNext
            ? '4px solid #10B981'
            : '1px solid rgba(255, 255, 255, 0.07)',
          boxShadow: isLive
            ? '0 0 24px rgba(255, 107, 53, 0.28)'
            : isNext
            ? '0 0 18px rgba(16, 185, 129, 0.22)'
            : 'none',
          borderRadius: { xs: 3, sm: 3.5 },
          transition: 'all 0.25s ease',
          '&:hover': {
            bgcolor: isLive
              ? 'rgba(255, 107, 53, 0.2)'
              : isNext
              ? 'rgba(16, 185, 129, 0.16)'
              : 'rgba(255, 255, 255, 0.05)',
            transform: { sm: 'translateX(4px)' },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.3, sm: 2 },
          }}
        >
          {/* Avatar com borda temática */}
          <Avatar
            src={avatarSrc}
            sx={{
              width: { xs: 50, sm: 58 },
              height: { xs: 50, sm: 58 },
              flexShrink: 0,
              bgcolor: isAutomatic ? 'rgba(255, 179, 0, 0.15)' : 'rgba(255, 107, 53, 0.15)',
              border: isLive
                ? '2.5px solid #FF6B35'
                : isNext
                ? '2.5px solid #10B981'
                : isAutomatic
                ? '2px solid rgba(255, 179, 0, 0.5)'
                : '2px solid rgba(255, 107, 53, 0.5)',
              boxShadow: isLive
                ? '0 0 12px rgba(255, 107, 53, 0.5)'
                : isNext
                ? '0 0 10px rgba(16, 185, 129, 0.4)'
                : 'none',
              color: isAutomatic ? '#FFB300' : '#FF6B35',
              fontSize: { xs: '0.8rem', sm: '0.95rem' },
              fontWeight: 800,
            }}
          >
            {isAutomatic ? 'AutoDj' : (djDisplayName.charAt(0) || 'DJ')}
          </Avatar>

          {/* Conteúdo do Programa */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Linha 1: Título e Badges */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 0.8,
                mb: 0.3,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  color: '#FFFFFF',
                  fontFamily: 'Orbitron, sans-serif',
                  fontSize: { xs: '0.88rem', sm: '1.05rem' },
                  letterSpacing: '0.3px',
                  lineHeight: 1.25,
                }}
              >
                {program.name || program.title}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
                {isLive && (
                  <Box
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.75)',
                      border: '1.2px solid #FF6B35',
                      borderRadius: '12px',
                      px: 0.9,
                      py: 0.2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      boxShadow: '0 0 10px rgba(255, 107, 53, 0.45)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#FF6B35',
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(0.8)', opacity: 0.7 },
                          '50%': { transform: 'scale(1.2)', opacity: 1 },
                          '100%': { transform: 'scale(0.8)', opacity: 0.7 },
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: '#FF6B35',
                        fontSize: { xs: '0.62rem', sm: '0.68rem' },
                        fontFamily: 'Orbitron, sans-serif',
                        letterSpacing: '0.5px',
                        lineHeight: 1,
                      }}
                    >
                      NO AR
                    </Typography>
                  </Box>
                )}

                {isNext && (
                  <Box
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.75)',
                      border: '1.2px solid #10B981',
                      borderRadius: '12px',
                      px: 0.9,
                      py: 0.2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      boxShadow: '0 0 8px rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#10B981',
                        boxShadow: '0 0 5px #10B981',
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: '#10B981',
                        fontSize: { xs: '0.62rem', sm: '0.68rem' },
                        fontFamily: 'Orbitron, sans-serif',
                        letterSpacing: '0.5px',
                        lineHeight: 1,
                      }}
                    >
                      A SEGUIR
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Linha 2: DJ */}
            <Typography
              variant="caption"
              sx={{
                color: '#FF8C5A',
                fontWeight: 800,
                fontSize: { xs: '0.78rem', sm: '0.85rem' },
                display: 'block',
                mb: 0.4,
                letterSpacing: '0.2px',
              }}
            >
              {isAutomatic ? djDisplayName : `DJ ${djDisplayName}`}
            </Typography>

            {/* Linha 3: Descrição com limitação elegante no mobile */}
            {program.description && (
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: { xs: '0.74rem', sm: '0.82rem' },
                  lineHeight: 1.35,
                  mb: 0.6,
                  display: '-webkit-box',
                  WebkitLineClamp: { xs: 2, sm: 3 },
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {program.description}
              </Typography>
            )}

            {/* Linha 4: Horário em cápsula elegante */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1,
                py: 0.3,
                borderRadius: '6px',
                bgcolor: isLive
                  ? 'rgba(255, 107, 53, 0.15)'
                  : isNext
                  ? 'rgba(16, 185, 129, 0.12)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: isLive
                  ? '1px solid rgba(255, 107, 53, 0.35)'
                  : isNext
                  ? '1px solid rgba(16, 185, 129, 0.3)'
                  : '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <Schedule sx={{ fontSize: 13, color: isLive ? '#FF6B35' : isNext ? '#10B981' : 'rgba(255, 255, 255, 0.6)' }} />
              <Typography
                variant="caption"
                sx={{
                  color: isLive ? '#FF6B35' : isNext ? '#10B981' : 'rgba(255, 255, 255, 0.85)',
                  fontWeight: 700,
                  fontSize: { xs: '0.74rem', sm: '0.8rem' },
                  fontFamily: "'Roboto Mono', monospace",
                  letterSpacing: '0.3px',
                }}
              >
                {formatTime(program.start_time)} - {formatTime(program.end_time)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  };

  // Render moderno e de alto contraste para slots de AutoDJ (Sem azul, horário em destaque dourado/âmbar)
  const renderAutoRow = (program) => {
    const isLive = checkIsLive(program);
    const isNext = checkIsNext(program);
    const name = program.name || program.title || 'Música Automática';

    return (
      <Box
        key={`autorow-${program.id || program._id || program.name}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.2, sm: 1.8 },
          px: { xs: 1.3, sm: 1.8 },
          py: { xs: 0.75, sm: 0.85 },
          mb: 0.7,
          borderRadius: '10px',
          bgcolor: isLive
            ? 'linear-gradient(90deg, rgba(255, 107, 53, 0.18) 0%, rgba(20, 20, 24, 0.95) 100%)'
            : isNext
            ? 'linear-gradient(90deg, rgba(16, 185, 129, 0.14) 0%, rgba(20, 20, 24, 0.95) 100%)'
            : 'linear-gradient(90deg, rgba(255, 179, 0, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
          border: isLive
            ? '1px solid rgba(255, 107, 53, 0.5)'
            : isNext
            ? '1px solid rgba(16, 185, 129, 0.45)'
            : '1px solid rgba(255, 255, 255, 0.08)',
          borderLeft: isLive
            ? '3.5px solid #FF6B35'
            : isNext
            ? '3.5px solid #10B981'
            : '3.5px solid #FFB300',
          boxShadow: isLive
            ? '0 0 15px rgba(255, 107, 53, 0.2)'
            : isNext
            ? '0 0 12px rgba(16, 185, 129, 0.15)'
            : '0 2px 6px rgba(0, 0, 0, 0.25)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            bgcolor: isLive
              ? 'rgba(255, 107, 53, 0.22)'
              : isNext
              ? 'rgba(16, 185, 129, 0.18)'
              : 'rgba(255, 179, 0, 0.1)',
            transform: { sm: 'translateX(3px)' },
            borderColor: isLive ? '#FF6B35' : isNext ? '#10B981' : 'rgba(255, 179, 0, 0.4)',
          },
        }}
      >
        {/* Badge de Horário em Destaque Alto Contraste (Ambar / Ouro vibrante, limpo e legível) */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: { xs: 1, sm: 1.2 },
            py: { xs: 0.35, sm: 0.4 },
            borderRadius: '6px',
            bgcolor: isLive
              ? 'rgba(255, 107, 53, 0.2)'
              : isNext
              ? 'rgba(16, 185, 129, 0.16)'
              : 'rgba(255, 179, 0, 0.12)',
            border: isLive
              ? '1px solid rgba(255, 107, 53, 0.45)'
              : isNext
              ? '1px solid rgba(16, 185, 129, 0.4)'
              : '1px solid rgba(255, 179, 0, 0.32)',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Roboto Mono', monospace",
              fontSize: { xs: '0.73rem', sm: '0.8rem' },
              fontWeight: 800,
              color: isLive ? '#FF6B35' : isNext ? '#10B981' : '#FFB300',
              letterSpacing: '0.3px',
              lineHeight: 1,
            }}
          >
            {formatTime(program.start_time)}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.76rem' },
              fontWeight: 800,
              color: isLive ? '#FF8C5A' : isNext ? '#34D399' : '#FFA000',
              lineHeight: 1,
              userSelect: 'none',
              mx: 0.1,
            }}
          >
            -
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Roboto Mono', monospace",
              fontSize: { xs: '0.73rem', sm: '0.8rem' },
              fontWeight: 800,
              color: isLive ? '#FF6B35' : isNext ? '#10B981' : '#FFB300',
              letterSpacing: '0.3px',
              lineHeight: 1,
            }}
          >
            {formatTime(program.end_time)}
          </Typography>
        </Box>

        {/* Nome do bloco / Playlist em Branco Nítido e Alto Contraste */}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: { xs: '0.8rem', sm: '0.88rem' },
            fontWeight: 700,
            color: isLive ? '#FFFFFF' : isNext ? '#FFFFFF' : '#FFFFFF',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            letterSpacing: '0.3px',
            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          }}
        >
          {name}
        </Typography>

        {/* Badge de status (NO AR / A SEGUIR) */}
        {isLive && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: '#FF6B35',
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(0.8)', opacity: 0.7 },
                  '50%': { transform: 'scale(1.2)', opacity: 1 },
                  '100%': { transform: 'scale(0.8)', opacity: 0.7 },
                },
              }}
            />
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: '#FF6B35', fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.5px' }}>
              NO AR
            </Typography>
          </Box>
        )}
        {isNext && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981', boxShadow: '0 0 6px #10B981' }} />
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: '#10B981', fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.5px' }}>
              A SEGUIR
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(12, 12, 16, 0.97)',
          backdropFilter: 'blur(24px)',
          border: '1.5px solid rgba(255, 107, 53, 0.35)',
          borderRadius: { xs: 3.5, sm: 4 },
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: '93vh', sm: '90vh' },
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.85), 0 0 35px rgba(255, 107, 53, 0.16)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      {/* Cabeçalho do Modal */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 107, 53, 0.22)',
          p: { xs: 1.5, sm: 2.2 },
          bgcolor: 'rgba(0, 0, 0, 0.25)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Radio sx={{ color: '#FF6B35', fontSize: { xs: 22, sm: 26 } }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Orbitron, sans-serif',
                fontWeight: 800,
                color: '#FF6B35',
                fontSize: { xs: '0.98rem', sm: '1.2rem' },
                letterSpacing: '0.5px',
                lineHeight: 1.2,
              }}
            >
              Programação da Semana
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.55)',
                fontSize: { xs: '0.68rem', sm: '0.75rem' },
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Horários no fuso oficial de Brasília (América/São Paulo)
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'rgba(255, 255, 255, 0.75)',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            '&:hover': { color: '#FF6B35', bgcolor: 'rgba(255, 107, 53, 0.15)' },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Tabs com os dias da semana (Formato moderno estilo app com botões em pílula) */}
      <Box
        sx={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          bgcolor: 'rgba(0, 0, 0, 0.4)',
          px: { xs: 1, sm: 2 },
          py: 0.8,
        }}
      >
        <Tabs
          value={selectedDay}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          allowScrollButtonsMobile
          sx={{
            minHeight: { xs: 40, sm: 44 },
            '& .MuiTabs-flexContainer': {
              gap: { xs: 0.7, sm: 1 },
            },
            '& .MuiTabs-indicator': {
              display: 'none', // Remove o traço tradicional em favor de botões tipo chip
            },
            '& .MuiTab-root': {
              minHeight: { xs: 38, sm: 42 },
              minWidth: { xs: 52, sm: 84 },
              px: { xs: 1.2, sm: 2 },
              py: { xs: 0.4, sm: 0.6 },
              borderRadius: '12px',
              textTransform: 'none',
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 700,
              fontSize: { xs: '0.8rem', sm: '0.88rem' },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(255, 255, 255, 0.07)',
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                color: '#FFFFFF',
              },
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, #FF6B35 0%, #E8590C 100%)',
                color: '#FFFFFF',
                border: '1px solid #FF8C5A',
                boxShadow: '0 4px 14px rgba(255, 107, 53, 0.45)',
                transform: 'translateY(-1px)',
              },
            },
          }}
        >
          {daysOfWeek.map((day, index) => {
            const isTodayTab = isCurrentDay(index);
            const isSelected = selectedDay === index;
            return (
              <Tab
                key={day.key}
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.78rem', sm: '0.86rem' },
                        fontWeight: 800,
                        lineHeight: 1.1,
                        color: isSelected ? '#FFFFFF' : 'inherit',
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{day.short}</Box>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{day.full}</Box>
                    </Typography>

                    {isTodayTab && (
                      <Box
                        sx={{
                          px: 0.6,
                          py: 0.1,
                          borderRadius: '4px',
                          bgcolor: isSelected ? '#FFFFFF' : '#39FF14',
                          color: '#000000',
                          fontSize: '0.52rem',
                          fontWeight: 900,
                          fontFamily: 'Orbitron, sans-serif',
                          lineHeight: 1,
                          letterSpacing: '0.3px',
                        }}
                      >
                        HOJE
                      </Box>
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Conteúdo rolável com lista de programas */}
      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '5px' },
          '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.25)' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 107, 53, 0.35)',
            borderRadius: '4px',
            '&:hover': { background: '#FF6B35' },
          },
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#FF6B35', mb: 2 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Carregando grade de programação...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#FF8A80', border: '1px solid #D32F2F' }}>
            Erro ao carregar programação: {error.message}
          </Alert>
        ) : (
          <>
            {/* 1. Programas com DJs */}
            {djPrograms.length > 0 && (
              <Box sx={{ mb: 2.8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.3 }}>
                  <Headphones sx={{ color: '#FF6B35', fontSize: { xs: 18, sm: 20 } }} />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: '#FF6B35',
                      fontFamily: 'Orbitron, sans-serif',
                      fontWeight: 800,
                      fontSize: { xs: '0.88rem', sm: '1rem' },
                      letterSpacing: '0.5px',
                      textShadow: '0 0 10px rgba(255, 107, 53, 0.3)',
                    }}
                  >
                    Programas dos DJs
                  </Typography>
                  <Chip
                    label={djPrograms.length}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 107, 53, 0.16)',
                      color: '#FF6B35',
                      border: '1px solid rgba(255, 107, 53, 0.3)',
                      fontWeight: 800,
                      fontSize: '0.68rem',
                      height: 20,
                    }}
                  />
                </Box>
                {djPrograms.map(program => renderProgramCard(program, false))}
              </Box>
            )}

            {/* 2. Programação do AutoDJ (blocos musicais cadastrados - visual âmbar dourado de alto contraste) */}
            {autoPrograms.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.3 }}>
                  <QueueMusic sx={{ color: '#FFB300', fontSize: { xs: 18, sm: 20 } }} />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: '#FFB300',
                      fontFamily: 'Orbitron, sans-serif',
                      fontWeight: 800,
                      fontSize: { xs: '0.88rem', sm: '1rem' },
                      letterSpacing: '0.5px',
                      textShadow: '0 0 12px rgba(255, 179, 0, 0.3)',
                    }}
                  >
                    Programação AutoDJ
                  </Typography>
                  <Chip
                    label={autoPrograms.length}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 179, 0, 0.16)',
                      color: '#FFB300',
                      border: '1px solid rgba(255, 179, 0, 0.35)',
                      fontWeight: 800,
                      fontSize: '0.68rem',
                      height: 20,
                    }}
                  />
                </Box>
                {autoPrograms.map(program => renderAutoRow(program))}
              </Box>
            )}

            {/* Mensagem quando não há programas no dia */}
            {djPrograms.length === 0 && autoPrograms.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <MusicNote sx={{ fontSize: 50, color: 'rgba(255, 255, 255, 0.25)', mb: 1.5 }} />
                <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600 }}>
                  Nenhuma programação cadastrada
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.45)' }}>
                  Para {daysOfWeek[selectedDay]?.full.toLowerCase()}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Rodapé do Modal */}
      <DialogActions
        sx={{
          p: { xs: 1.2, sm: 1.8 },
          borderTop: '1px solid rgba(255, 107, 53, 0.2)',
          bgcolor: 'rgba(0, 0, 0, 0.35)',
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="medium"
          sx={{
            borderColor: '#FF6B35',
            color: '#FF6B35',
            borderRadius: '24px',
            px: { xs: 4, sm: 5 },
            py: { xs: 0.6, sm: 0.8 },
            fontWeight: 800,
            fontSize: { xs: '0.84rem', sm: '0.9rem' },
            fontFamily: 'Orbitron, sans-serif',
            letterSpacing: '0.5px',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#FF8A65',
              bgcolor: 'rgba(255, 107, 53, 0.18)',
              boxShadow: '0 0 15px rgba(255, 107, 53, 0.3)',
            },
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProgrammingModal;
